
import { Link, useNavigate } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  PlayCircleIcon,
  ChartBarIcon,
  CpuChipIcon,
  ArrowRightIcon,
  UserGroupIcon,
  BoltIcon,
  StarIcon,
  CurrencyDollarIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ShieldCheckIcon,
  ArrowTrendingUpIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  ClockIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useCases } from '../../configs/useCases';
import { site } from '../../configs/site';
import HeroSwoopBackground from '../../components/UseCaseComponents/HeroSwoopBackground';
import QuickTryCard from '../../components/UseCaseComponents/QuickTryCard';
import SiteFooter from '../../components/SiteFooter/SiteFooter';
import HowItRunsBand from '../../components/BiSections/HowItRunsBand';
import FundingBand from '../../components/BiSections/FundingBand';
import { preloadHubSpotFormsScript } from '../../services/HubSpotForms';

// Maps a use case's `icon` key to the glyph shown on its card.
// Falls back to CpuChipIcon (already used elsewhere in the app for AI/Q features)
// so a use case can omit `icon` without breaking the layout.
const useCaseIcons = {
  chat: ChatBubbleLeftRightIcon,
  video: PlayCircleIcon,
  dashboard: ChartBarIcon,
  site: GlobeAltIcon,
  report: DocumentTextIcon,
};

// Maps a journey stage's `icon` key (site.journey.stages) to its glyph.
const stageIcons = {
  workshop: ChatBubbleOvalLeftEllipsisIcon,
  prototype: DocumentTextIcon,
  production: ShieldCheckIcon,
  support: ArrowTrendingUpIcon,
  team: UserGroupIcon,
};

// Maps a hero badge's `icon` key (site.heroBadges) to its glyph.
const badgeIcons = {
  star: StarIcon,
  dollar: CurrencyDollarIcon,
  bolt: BoltIcon,
  clock: ClockIcon,
  shield: ShieldCheckIcon,
};

// Tag pill colors for site.workflow steps.
const workflowTagClasses = {
  'AI-led': 'bg-[#F3E8FF] text-[#6B21A8]',
};
const defaultWorkflowTagClass = 'bg-primary-50 text-primary-600';

// Hero feature row when site.heroBadges isn't set (Quick site default).
const defaultHeroBadges = [
  { icon: 'star', label: 'AWS Advanced Tier Partner' },
  { icon: 'dollar', label: 'Backed by AWS funding' },
  { icon: 'bolt', label: 'Built on Amazon Quick' },
];

const Home = () => {
  const navigate = useNavigate();
  // Fill the sample slots from the same configuration as the sidebar.
  // site.showUseCaseGrid: false turns that grid off (the BI site's platform
  // cards below already link to every use case).
  const cards = site.showUseCaseGrid === false ? [] : useCases;

  const heroPrimary = site.heroPrimary || { label: 'See how it works', target: 'journey' };
  const heroBadges = site.heroBadges || defaultHeroBadges;

  const scrollToHeroTarget = () => {
    document.getElementById(heroPrimary.target)?.scrollIntoView({ behavior: 'smooth' });
  };


  return (
    <div className="flex flex-1 flex-col">
    {/* Hero -- the exact same swoop/haze background box as the use-case
        pages' heroBanner (see HeroSwoopBackground / UseCaseView). Getting
        the SAME container padding wasn't actually enough on its own: all
        three use-case pages render this box at an identical height not
        because of a min-height, but because their right-hand column always
        holds a fixed-aspect-ratio VideoEmbed (aspect-[16/7.8], lg:w-4/5),
        and that's what's actually setting the box's final height -- their
        text content (title/description length) barely affects it. Home has
        no per-page video, so without something occupying that same slot,
        its box comes out shorter, which scales/crops the SVG differently
        (a shorter box means the "slice" fill zooms in less, showing a much
        bigger, more saturated chunk of the ribbon graphic than the other
        pages do). Fix: mirror the exact same two-column grid, with an
        invisible spacer in the right column carrying the identical
        aspect-ratio/width classes as VideoEmbed, so the box lands at the
        same height -- and therefore the same SVG crop/zoom -- at every
        viewport width, not just one hardcoded pixel value. */}
    <div className="relative isolate overflow-hidden bg-[#e7e6e1] px-4 pb-10 pt-4 sm:px-8 sm:pb-14 sm:pt-6 lg:px-14">
      {/* Same mobile treatment as the use-case hero band (see UseCaseView):
          "xMax" keeps the crop over the colorful ribbons instead of the
          flat gray on a narrow/tall mobile box. No mobileBlurPx now that
          the title/description sit in their own solid card instead of
          directly on the graphic -- the swoosh underneath can stay
          sharp/unblurred. */}
      <HeroSwoopBackground mobileAlign="xMax" />

      {/* Invisible spacer matching the size of the "Back to use cases" pill
          (+ its mb-6) that sits at the top of this same box on a use-case
          page (see UseCaseView) -- Home doesn't need that link since it IS
          the home page, but without something reserving that same height,
          this box comes out ~62px shorter than the use-case pages', which
          is the last remaining source of the box (and therefore the SVG
          crop) not matching exactly. `invisible` keeps the layout space
          without painting or being reachable by keyboard/AT. */}
      <div
        className="invisible mb-6 flex w-fit items-center gap-2 rounded-md border px-4 py-2 text-sm font-semibold"
        aria-hidden="true"
      >
        <span>&larr;</span>
        Back to use cases
      </div>

      <div className="relative grid grid-cols-1 items-center gap-10 lg:min-h-[26rem] lg:grid-cols-2">
        {/* Below sm, the whole left column (title, description, buttons,
            feature row) now sits in a mostly-solid white card on top of
            the blurred swoosh -- same treatment as the use-case hero band
            (see UseCaseView), modeled on how the West Loop Strategy
            marketing site's own mobile hero handles this: a near-opaque
            (95%) white card rather than white text + shadow directly on
            the graphic. Resets to fully transparent/no padding at sm and
            up, where the plain desktop crop already reads fine with plain
            text and no card. */}
        <div className="rounded-xl border border-black/10 bg-white/90 p-5 shadow-lg sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
          <h1
            className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl sm:text-black lg:text-5xl"
            style={{ textWrap: 'balance' }}
          >
            {site.title}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-gray-600 sm:text-lg sm:text-black">{site.description}</p>
          {site.tagline && (
            <p className="mt-4 max-w-2xl text-base font-semibold text-gray-800 sm:text-lg sm:text-black">
              {site.tagline}
            </p>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={scrollToHeroTarget}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
            >
              {heroPrimary.label}
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </button>

            {site.contact.enabled && (
              <button
                type="button"
                onClick={() => navigate('/contact')}
                // See services/HubSpotForms.js -- starts fetching the
                // contact form's HubSpot script as soon as a visitor's
                // finger/cursor reaches this button, instead of only after
                // they've already landed on /contact.
                onMouseEnter={preloadHubSpotFormsScript}
                onTouchStart={preloadHubSpotFormsScript}
                className="inline-flex items-center gap-2 rounded-lg border border-black/20 bg-white/90 px-5 py-3 text-sm font-semibold text-gray-800 shadow-sm backdrop-blur-sm transition hover:bg-white"
              >
                {site.contact.buttonLabel}
              </button>
            )}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-700 sm:text-black/80">
            {heroBadges.map((badge) => {
              const BadgeIcon = badgeIcons[badge.icon] || BoltIcon;
              return (
                <span key={badge.label} className="inline-flex items-center gap-2">
                  <BadgeIcon className="h-5 w-5 text-primary-700" aria-hidden="true" />
                  {badge.label}
                </span>
              );
            })}
          </div>
        </div>

        {/* Fills the same slot a use-case page's tilted VideoEmbed occupies
            (see the box-height comment above) -- but here it's a real,
            always-on CTA rather than a spacer, since Home has no per-page
            video of its own. Unlike the old invisible spacer this one is
            NOT hidden below lg: whoever scans the site's own QR code at the
            booth is likely on a phone, so this needs to show up there too,
            just stacked under the text column instead of beside it (see the
            grid-cols-1 lg:grid-cols-2 above). site.quickTry.enabled can turn
            this off without touching this file.

            aspectClassName is plain "aspect-auto" at every breakpoint now --
            the fixed 16/7.8 ratio (fine for a video, which just
            object-covers whatever box it's given) was forcing this card's
            real text content -- icon, two-line heading, description, button
            -- into a box shorter than that content actually needs, so it
            was overflowing and getting clipped by the card's own
            overflow-hidden. That wasn't just a narrow-mobile problem: even
            at this column's normal desktop width, 16/7.8 comes out shorter
            than the content's natural height, cutting off the bottom of the
            button (inconsistently, since the exact crop point shifts with
            font rendering/line-wrapping). "aspect-auto" everywhere lets the
            box size to its content instead, at every screen width -- the
            hero row's own lg:min-h-[26rem] (see the grid above) still keeps
            the whole band from collapsing shorter than that on desktop.

            max-w-xl caps the card's own width -- w-full/lg:w-4/5 alone means
            it keeps growing with whatever it's a percentage of (the whole
            hero column on desktop, the full content width in the
            single-column tablet/mobile layout below lg), and since the
            height is content-driven (aspect-auto, not tied to width), a
            wide-enough box of either kind stretched the card into a long,
            flat strip -- not just on an unusually wide desktop monitor, but
            also at plain tablet-ish widths (below lg, where this is still
            full-width) once the window's wide enough. The cap applies at
            every breakpoint now (previously only lg:), and only kicks in
            once the box would otherwise exceed 36rem -- a no-op at normal
            phone/laptop widths, and safe to apply everywhere since it only
            ever makes the box NARROWER, never forces it into a fixed
            height the way the old aspect ratio did, so there's no way for
            it to push the text/button off the edge or clip anything.
            mx-auto centers the (now narrower) box in that single-column
            layout instead of leaving it pinned to the left; lg:mr-0
            cancels that auto right-margin once lg:ml-auto takes over to
            right-align it in the two-column desktop layout instead. */}
        {site.quickTry?.enabled ? (
          <QuickTryCard
            title={site.quickTry.title}
            description={site.quickTry.description}
            buttonLabel={site.quickTry.buttonLabel}
            url={site.quickTry.url}
            icon={site.quickTry.icon === 'clock' ? ClockIcon : undefined}
            className="mx-auto w-full max-w-xl lg:ml-auto lg:mr-0 lg:w-4/5"
            aspectClassName="aspect-auto"
          />
        ) : (
          <div
            className="hidden aspect-[16/7.8] w-full lg:ml-auto lg:block lg:w-4/5"
            aria-hidden="true"
          />
        )}
      </div>
    </div>

    {/* Five-stage engagement journey (site.journey). Same card treatment
        as the use-case cards below (ring instead of border -- see the note
        on those cards). */}
    {site.journey?.enabled && (
      <section id="journey" className="scroll-mt-20 px-4 pb-4 pt-8 sm:px-6 sm:pt-10 lg:px-10 lg:pt-14">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 sm:text-[28px]">{site.journey.title}</h2>
          <p className="mt-1 text-sm text-gray-500">{site.journey.description}</p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {site.journey.stages.map((stage, index) => {
            const Icon = stageIcons[stage.icon] || CpuChipIcon;
            const stageNumber = String(index + 1).padStart(2, '0');

            return (
              <article
                key={stage.title}
                className="flex flex-col gap-3 rounded-xl bg-white p-5 shadow-sm ring-1 ring-inset ring-gray-200"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50">
                  <Icon className="h-5 w-5 text-primary-600" aria-hidden="true" />
                </span>
                <div className="text-xs font-semibold uppercase tracking-wider text-primary-600">
                  Stage {stageNumber}
                </div>
                <h3 className="text-[17px] font-semibold leading-snug text-gray-900">{stage.title}</h3>
                <p className="text-sm leading-relaxed text-gray-600">{stage.description}</p>
                <ul className="flex list-disc flex-col gap-1.5 pl-[18px] text-[13px] leading-normal text-gray-700">
                  {stage.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                <div className="mt-auto border-t border-gray-100 pt-3 text-[13px] leading-normal text-gray-700">
                  <span className="font-semibold">You get:</span> {stage.outcome}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    )}

    {/* Migration workflow (site.workflow): numbered steps with
        Automated / AI-led tags, a "repeats per wave" note, and the
        typical-vs-West-Loop comparison. */}
    {site.workflow?.enabled && (
      <section id="workflow" className="scroll-mt-20 flex flex-col gap-6 px-4 pb-2 pt-8 sm:px-6 sm:pt-10 lg:px-10 lg:pt-14">
        <div className="flex max-w-3xl flex-col gap-1.5">
          <div className="text-xs font-semibold uppercase tracking-wider text-primary-600">{site.workflow.eyebrow}</div>
          <h2 className="text-2xl font-bold text-gray-900 sm:text-[28px]">{site.workflow.title}</h2>
          <p className="text-[15px] leading-relaxed text-gray-600">{site.workflow.description}</p>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {site.workflow.steps.map((step, index) => (
            <article
              key={step.title}
              className="flex flex-col gap-3 rounded-xl bg-white p-5 shadow-sm ring-1 ring-inset ring-gray-200"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-600 text-sm font-bold text-white">
                  {index + 1}
                </span>
                <span className="h-0.5 flex-1 bg-indigo-200" aria-hidden="true" />
                <ArrowRightIcon className="h-4 w-4 text-indigo-400" aria-hidden="true" />
              </div>
              <span
                className={`self-start rounded-full px-2.5 py-0.5 text-xs font-semibold ${workflowTagClasses[step.tag] || defaultWorkflowTagClass}`}
              >
                {step.tag}
              </span>
              <h3 className="text-[17px] font-semibold leading-snug text-gray-900">{step.title}</h3>
              <p className="text-sm leading-relaxed text-gray-600">{step.description}</p>
              <ul className="flex list-disc flex-col gap-1.5 pl-[18px] text-[13px] leading-normal text-gray-700">
                {step.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        {site.workflow.repeatNote && (
          <div className="flex items-center gap-3 rounded-xl border border-dashed border-[#C4B5FD] bg-[#F3F0FB] px-5 py-3.5 text-sm leading-normal text-gray-700">
            <ArrowPathIcon className="h-5 w-5 shrink-0 text-[#6B21A8]" aria-hidden="true" />
            <span>
              <span className="font-semibold text-gray-900">{site.workflow.repeatNote.lead}</span>{' '}
              {site.workflow.repeatNote.text}
            </span>
          </div>
        )}

        {site.workflow.compare && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-2.5 rounded-xl bg-gray-50 p-5 ring-1 ring-inset ring-gray-200">
              <div className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                {site.workflow.compare.typical.title}
              </div>
              <ul className="flex list-disc flex-col gap-1.5 pl-[18px] text-sm leading-normal text-gray-700">
                {site.workflow.compare.typical.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col gap-2.5 rounded-xl bg-indigo-50 p-5 ring-1 ring-inset ring-indigo-200">
              <div className="text-xs font-semibold uppercase tracking-wider text-primary-600">
                {site.workflow.compare.ours.title}
              </div>
              <ul className="flex list-disc flex-col gap-1.5 pl-[18px] text-sm leading-normal text-gray-800">
                {site.workflow.compare.ours.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>
    )}

    {/* Platform cards (site.platforms copy, useCases.js entries). Each
        opens that platform's use case page. */}
    {site.platforms?.enabled && (
      <section id="platforms" className="scroll-mt-20 flex flex-col gap-6 px-4 pb-6 pt-10 sm:px-6 lg:px-10 lg:pt-14">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 sm:text-[28px]">{site.platforms.title}</h2>
          <p className="mt-1 text-sm text-gray-500">{site.platforms.description}</p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {useCases.map((platform) => (
            // The whole card is the link (same as the use case cards), not
            // just the "Explore ..." line at the bottom. Always routes
            // through /:useCaseId.
            <Link
              key={platform.id}
              to={`/${encodeURIComponent(platform.id)}`}
              className="group flex flex-col gap-3 rounded-xl bg-white p-5 shadow-sm ring-1 ring-inset ring-gray-200 transition will-change-transform hover:-translate-y-0.5 hover:shadow-md hover:ring-primary-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-sm font-bold text-primary-600">
                {platform.abbr}
              </span>
              <h3 className="text-[17px] font-semibold leading-snug text-gray-900">{platform.title}</h3>
              <p className="text-sm leading-relaxed text-gray-600">{platform.description}</p>
              <ul className="flex list-disc flex-col gap-1.5 pl-[18px] text-[13px] leading-normal text-gray-700">
                {platform.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
              <span className="mt-auto inline-flex min-h-[44px] items-center gap-1.5 pt-2 text-sm font-semibold text-primary-700 group-hover:text-primary-800">
                Explore {platform.name} migrations
                <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </section>
    )}

    {/* Four-step summary band (site.howItRuns) and AWS funding band
        (site.funding) -- shared with the platform pages. */}
    <HowItRunsBand />
    <FundingBand />

    {/* Demo use cases -- hidden until useCases.js has entries, so the page
        doesn't show an empty "coming soon" block under the journey. */}
    {cards.length > 0 && (
    <div id="use-cases" className="scroll-mt-20 px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10">
      {/* Use cases section */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 sm:text-[28px]">Explore use cases</h2>
          <p className="mt-1 text-sm text-gray-500">
            Interactive demos built to answer real business questions.
          </p>
        </div>
      </div>

      {cards.length === 0 && (
        <p className="rounded-xl border border-dashed border-gray-300 bg-white/70 px-6 py-10 text-center text-base text-gray-600">
          New demos are coming soon.
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((card) => {
          const Icon = useCaseIcons[card.icon] || CpuChipIcon;

          return (
            <button
              key={card.id}
              // Always route through /:useCaseId (App.jsx's availableRoutes)
              // -- that's the only pattern registered for UseCaseView, which
              // looks entries up by `id`. Each use case's own `route` field
              // (e.g. '/sample-chat') doesn't match any configured route, so
              // using it here was silently falling through to Content.jsx's
              // catch-all "*" route, which redirects to the first configured
              // dashboard -- that's why every card, old and new, was landing
              // on /dashboard/....
              onClick={() => navigate(`/${encodeURIComponent(card.id)}`)}
              // will-change-transform forces this card onto its own GPU
              // layer from the initial paint (not just once you hover it).
              // Uses ring (box-shadow) instead of border for the outline --
              // a real `border` on a GPU-composited, rounded element can
              // round to 0 physical pixels on some edges but not others
              // depending on display scaling/zoom, making the top/left
              // edge vanish while bottom/right survive. ring-inset doesn't
              // hit that rounding bug since it's painted as a shadow, not a
              // layout-affecting border.
              className="group flex flex-col gap-3 rounded-xl bg-white p-6 text-left shadow-sm ring-1 ring-inset ring-gray-200 transition will-change-transform hover:-translate-y-0.5 hover:shadow-md hover:ring-primary-200"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-50">
                <Icon className="h-6 w-6 text-primary-600" aria-hidden="true" />
              </span>

              <h3 className="text-base font-semibold text-gray-900">
                {card.title}
              </h3>

              <p className="text-sm text-gray-500">
                {card.description || 'Coming soon.'}
              </p>

              <span className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-primary-700">
                View demo
                <ArrowRightIcon
                  className="h-4 w-4 transition group-hover:translate-x-1"
                  aria-hidden="true"
                />
              </span>
            </button>
          );
        })}

      </div>
    </div>
    )}

    <SiteFooter />
    </div>
  );
};

export default Home;
